/*
 * Admin Component with Access Control
 * 
 * This component provides admin functionality and is protected by email-based authentication.
 * 
 * Setup:
 * 1. Set REACT_APP_ADMIN and REACT_APP_ARI_ADMIN environment variables in .env file
 * 2. Only users logged in with either of the specified admin emails can access this component
 * 3. Non-admin users are redirected to /login
 * 4. Non-authenticated users are also redirected to /login
 * 
 * Security Features:
 * - Email verification against both admin environment variables
 * - Automatic redirection for unauthorized access
 * - User feedback for access denied scenarios
 */

import React, { useState, useEffect } from "react";
import EnhancedAdminBlogForm from "../EnhancedAdminBlogForm";
import AdminCreateProductForm from "../AdminCreateProductForm";
import AdminCreateAppointmentForm from "../AdminCreateEventForm";
import AdminCreateCampaignForm from "../AdminCreateCampaignForm";
import EventsManageTable from "../EventsManageTable";
import AdminDesign from "../AdminDesign";
import MostreTable from "../MostreTable";
import MyDashboard from "../MyDashboard";
import NotificationBell from "../NotificationBell";
import LibraryAdmin from "../LibraryAdmin";
import UsersTable from "../UsersTable";
import RankRequestsTable from "../RankRequestsTable";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Logout from '../Logout';
import { useLanguage } from '../../contexts/LanguageContext';

import {
  FileText,
  Package,
  Calendar,
  Megaphone,
  BarChart3,
  Plus,
  Filter,
  Settings,
  User,
  ChevronDown,
  Home,
  Menu,
  LogOut,
  X,
  BookOpen,
  TrendingUp,
  Palette
} from "lucide-react";
import "./Admin.scss";
import "./TheOilsBabe.css";

const ADMIN_EMAILS = [
  process.env.REACT_APP_ADMIN,
  process.env.REACT_APP_ARI_ADMIN,
]
  .filter(Boolean)
  .map((email) => email.trim().toLowerCase());

const isConfiguredAdmin = (email) =>
  ADMIN_EMAILS.includes(String(email || "").trim().toLowerCase());

const isDesktopOrTablet = () => {
  if (typeof window !== 'undefined') {
    return window.innerWidth >= 768;
  }
  return true;
};

const Admin = () => {
  const [step, setStep] = useState(1);
  const { user } = useSelector((store) => store);
  const history = useHistory();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [userDropdownOpenMobile, setUserDropdownOpenMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(isDesktopOrTablet());
  const { language } = useLanguage();

  // Admin access control
  useEffect(() => {
    const isAdmin = isConfiguredAdmin(user?.user?.email);

    console.log('Admin access check:', {
      hasUser: !!user,
      hasUserObj: !!(user && user.user),
      userEmail: user?.user?.email,
      adminEmail: process.env.REACT_APP_ADMIN,
      ariAdminEmail: process.env.REACT_APP_ARI_ADMIN,
      isAdmin: isAdmin
    });

    // Check if user is logged in
    if (!user || !user.user) {
      console.log('User not logged in, redirecting to login');
      history.push('/login');
      return;
    }

    // Check if user email matches either admin email
    if (!isAdmin) {
      console.log('User is not admin, redirecting to login');
      history.push('/login');
      return;
    }
  }, [user, history]);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(isDesktopOrTablet());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // If user is not admin or not logged in, show loading or nothing
  if (!user || !user.user) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontFamily: 'Arial, sans-serif' 
      }}>
        <div>Redirecting to login...</div>
      </div>
    );
  }

  if (!isConfiguredAdmin(user.user.email)) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontFamily: 'Arial, sans-serif',
        flexDirection: 'column' 
      }}>
        <div style={{ marginBottom: '20px' }}>Access Denied</div>
        <div style={{ fontSize: '14px', color: '#666' }}>You do not have permission to access this page.</div>
      </div>
    );
  }

  const setDisplayName = () => {
    let name;
    if (user.user.displayName) name = user.user.displayName;
    return name;
  };

  const menuItems = [
    { id: 10, label: 'Design', icon: Palette, description: 'Personalizare pagina principală', title: 'Design Homepage' },
    { id: 1, label: 'Blog', icon: FileText, description: 'Gestionare conținut blog', title: 'Adaugă blog' },
    { id: 2, label: 'Produs', icon: Package, description: 'Catalog produse', title: 'Adaugă produs' },
    { id: 3, label: 'Eveniment', icon: Calendar, description: 'Gestionare evenimente', title: 'Adaugă eveniment' },
    { id: 4, label: 'Campanie', icon: Megaphone, description: 'Campanii marketing', title: 'Adaugă campanie' },
    { id: 5, label: 'Mostre', icon: Package, description: 'Gestionare mostre din toate campaniile', title: 'Mostre' },
    { id: 6, label: 'Tablou de bord', icon: BarChart3, description: 'Privire generală analize', title: 'Tablou de bord' },
    { id: 7, label: 'Librǎria mea', icon: BookOpen, description: 'Gestionare videouri, eBook-uri și Q&A', title: 'Librǎria mea' },
    { id: 8, label: 'Utilizatori', icon: User, description: 'Gestionare utilizatori înregistrați', title: 'Gestionare utilizatori' },
    { id: 9, label: 'Cereri Rank', icon: TrendingUp, description: 'Aprobare cereri de promovare', title: 'Cereri de Promovare' }
  ];

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Navigation handlers for dropdown menu
  const handleProfileClick = () => {
    history.push('/profil');
    setUserDropdownOpen(false);
    setUserDropdownOpenMobile(false);
  };

  const handleSettingsClick = () => {
    history.push('/settings');
    setUserDropdownOpen(false);
    setUserDropdownOpenMobile(false);
  };

  const handleLogout = () => {
    // Logout functionality will be handled by the Logout component
    setUserDropdownOpen(false);
    setUserDropdownOpenMobile(false);
  };

  const renderContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="content-section content-section--blog">
            <div className="content-header">
              <div className="header-left">
                <h1 className="page-title">Adaugă blog</h1>
                <p className="page-subtitle">Creează și gestionează articolele de blog</p>
              </div>
            </div>

            <div className="admin-module admin-module--blog">
              <EnhancedAdminBlogForm authorName={setDisplayName()} />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="content-section">
            <div className="content-header">
              <div className="header-left">
                <h1 className="page-title">Adaugă produs</h1>
                <p className="page-subtitle">Gestionează catalogul de produse</p>
              </div>
              <div className="header-actions">
                <button className="btn btn-secondary">
                  <Filter size={16} />
                  Filtru
                </button>
                <button className="btn btn-primary">
                  <Plus size={16} />
                  Adaugă produs
                </button>
              </div>
            </div>

            <div className="admin-form-container">
              <div className="form-card">
                {/* <div className="form-header">
                  <h3>Create New Product</h3>
                  <div className="form-actions-header">
                    <button className="btn-icon" title="Save Draft">
                      <Save size={18} />
                    </button>
                    <button className="btn-icon" title="Preview">
                      <Edit size={18} />
                    </button>
                  </div>
                </div> */}

                <div className="admin-form">
                  <AdminCreateProductForm />
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="content-section">
            <div className="content-header">
              <div className="header-left">
                <h1 className="page-title">Adaugă eveniment</h1>
                <p className="page-subtitle">Creează și gestionează evenimente</p>
              </div>
              <div className="header-actions">
                <button className="btn btn-secondary">
                  <Filter size={16} />
                  Filtru
                </button>
                <button className="btn btn-primary">
                  <Plus size={16} />
                  Eveniment nou
                </button>
              </div>
            </div>

            <div className="admin-form-container">
              <div className="form-card">
                {/* <div className="form-header">
                  <h3>Create New Event</h3>
                  <div className="form-actions-header">
                    <button className="btn-icon" title="Save Draft">
                      <Save size={18} />
                    </button>
                    <button className="btn-icon" title="Preview">
                      <Edit size={18} />
                    </button>
                  </div>
                </div> */}

                <div className="admin-form">
                  <AdminCreateAppointmentForm />
                </div>
              </div>
              <div className="form-card" style={{ marginTop: '1.5rem' }}>
                <div className="admin-form">
                  <EventsManageTable />
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="content-section">
            <div className="content-header">
              <div className="header-left">
                <h1 className="page-title">Adaugă campanie</h1>
                <p className="page-subtitle">Gestionează campaniile de marketing</p>
              </div>
              <div className="header-actions">
                <button className="btn btn-secondary">
                  <Filter size={16} />
                  Filtru
                </button>
                <button className="btn btn-primary">
                  <Plus size={16} />
                  Campanie nouă
                </button>
              </div>
            </div>

            <div className="admin-form-container">
              <div className="form-card">
                {/* <div className="form-header">
                  <h3>Create New Campaign</h3>
                  <div className="form-actions-header">
                    <button className="btn-icon" title="Save Draft">
                      <Save size={18} />
                    </button>
                    <button className="btn-icon" title="Preview">
                      <Edit size={18} />
                    </button>
                  </div>
                </div> */}

                <div className="admin-form">
                  <AdminCreateCampaignForm />
                </div>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="content-section-samples">
            <div className="content-header">
              <div className="header-left">
                <h1 className="page-title">Mostre</h1>
                <p className="page-subtitle">Gestionează toate mostrele din toate campaniile</p>
              </div>
            </div>

            <div className="samples-container">
              <MostreTable />
            </div>
          </div>
        );

      case 6:
        return (
          <div className="content-section-dashboard">
            <div className="content-header">
              <div className="header-left">
                <h1 className="page-title">Tablou de bord</h1>
                <p className="page-subtitle">Privire generală asupra performanțelor</p>
              </div>
            </div>

            <div className="admin-module admin-module--dashboard">
              <div className="dashboard-wrapper">
                <MyDashboard />
              </div>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="content-section">
            <div className="content-header">
              <div className="header-left">
                <h1 className="page-title">Libraria mea</h1>
                <p className="page-subtitle">Gestionează videouri, eBook-uri și secțiuni Q&A</p>
              </div>
            </div>

            <div className="admin-module admin-module--library">
              <LibraryAdmin />
            </div>
          </div>
        );

      case 8:
        return (
          <div className="content-section">
            <div className="content-header">
              <div className="header-left">
                <h1 className="page-title">Gestionare utilizatori</h1>
                <p className="page-subtitle">Gestionează utilizatori înregistrați în aplicație</p>
              </div>
              <div className="header-actions">
                <button className="btn btn-secondary">
                  <Filter size={16} />
                  Filtre avansate
                </button>
                <button className="btn btn-primary">
                  <Settings size={16} />
                  Configurare
                </button>
              </div>
            </div>

            <div className="admin-module admin-module--users">
              <UsersTable />
            </div>
          </div>
        );

      case 9:
        return (
          <div className="content-section">
            <div className="content-header">
              <div className="header-left">
                <h1 className="page-title">Cereri de Promovare</h1>
                <p className="page-subtitle">Gestionează cererile de promovare la rank-uri superioare</p>
              </div>
            </div>

            <div className="admin-module admin-module--rank-requests">
              <RankRequestsTable />
            </div>
          </div>
        );

      case 10:
        return (
          <div className="content-section">
            <div className="content-header">
              <div className="header-left">
                <h1 className="page-title">Design Homepage</h1>
                <p className="page-subtitle">Personalizează elementele vizuale ale paginii principale</p>
              </div>
            </div>

            <div className="admin-form-container">
              <div className="form-card">
                <div className="admin-form">
                  <AdminDesign />
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="admin-container">
      {/* Sidebar - Hidden on Mobile */}
      {isDesktop && (
        <div className={`admin-sidebar tablet-desktop-only ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <div className="logo-section">
              {!sidebarCollapsed && (
                <div className="logo-home" style={{ cursor: 'pointer' }} onClick={() => history.push('/')}> 
                  <Home size={24} />
                </div>
              )}
              {!sidebarCollapsed && (
                <h2 className="brand-name">Panou</h2>
              )}
            </div>
            <button
              className="sidebar-toggle"
              onClick={toggleSidebar}
            >
              <Menu size={20} />
            </button>
          </div>

          <nav className="sidebar-nav">
            <ul className="nav-list">
              {menuItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <li key={item.id} className="nav-item">
                    <button
                      className={`nav-link ${step === item.id ? 'active' : ''}`}
                      onClick={() => setStep(item.id)}
                    >
                      <IconComponent size={20} className="nav-icon" />
                      {!sidebarCollapsed && (
                        <div className="nav-content">
                          <span className="nav-label">{item.label}</span>
                          <span className="nav-description">{item.description}</span>
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="sidebar-footer">
            {!sidebarCollapsed && (
              <div className="user-info">
                <div className="user-avatar">
                  <User size={16} />
                </div>
                <div className="user-details">
                  <span className="user-name">{setDisplayName() || 'Utilizator Admin'}</span>
                  <span className="user-role">Administrator</span>
                </div>
              </div>
            )}
            <Logout className="logout-btn">
              <LogOut size={18} />
              {!sidebarCollapsed && <span>Deconectare</span>}
            </Logout>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="admin-main">
        {/* Top Header */}
        <header className="admin-header">
          <div className="header-left">
            {/* Mobile Home Button (moved here, mobile-only) */}
            <a href="/" className="admin-brand-mobile mobile-only" style={{ display: 'inline-block' }}>
              <div className="logo-home">
                <Home size={20} />
              </div>
            </a>
            {/* <div className="search-container desktop-only">
              <Search size={18} className="search-icon" />
              <input
                type="text"
                placeholder="Caută..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div> */}
          </div>

          <div className="header-right">
            <div className="header-actions">
              {/* Mobile Menu Button (Hamburger) */}
              {/* <button
                className="mobile-menu-btn mobile-only"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <Menu size={20} />
              </button> */}
                              <button
                  className="user-menu-btn mobile-only"
                  style={{ padding: 6 }}
                  onClick={() => setUserDropdownOpenMobile(!userDropdownOpenMobile)}
                >
                  <div className="user-avatar-small">
                    <User size={14} />
                  </div>
                  <ChevronDown size={12} />
                </button>
                {userDropdownOpenMobile && (
                  <div className="user-dropdown modern-dropdown">
                    <div className="dropdown-item modern-item" onClick={handleProfileClick}>
                      <User size={16} />
                      <span>{language === 'en' ? 'Profile' : 'Profil'}</span>
                    </div>
                    <div className="dropdown-item modern-item" onClick={handleSettingsClick}>
                      <Settings size={16} />
                      <span>{language === 'en' ? 'Settings' : 'Setări'}</span>
                    </div>
                    <div className="dropdown-divider"></div>
                    <div className="dropdown-item modern-item logout-item" onClick={handleLogout}>
                      <Logout className="logout-component">
                        <LogOut size={16} />
                        <span>{language === 'en' ? 'Logout' : 'Deconectare'}</span>
                      </Logout>
                    </div>
                  </div>
                )}
              {/* Desktop User Menu */}
              <div className="user-menu desktop-only">
                <button
                  className="user-menu-btn"
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                >
                  <div className="user-avatar-small">
                    <User size={16} />
                  </div>
                  <ChevronDown size={14} />
                </button>

                {userDropdownOpen && (
                  <div className="user-dropdown modern-dropdown">
                    <div className="dropdown-item modern-item" onClick={handleProfileClick}>
                      <User size={16} />
                      <span>{language === 'en' ? 'Profile' : 'Profil'}</span>
                    </div>
                    <div className="dropdown-item modern-item" onClick={handleSettingsClick}>
                      <Settings size={16} />
                      <span>{language === 'en' ? 'Settings' : 'Setări'}</span>
                    </div>
                    <div className="dropdown-divider"></div>
                    <div className="dropdown-item modern-item logout-item" onClick={handleLogout}>
                      <Logout className="logout-component">
                        <LogOut size={16} />
                        <span>{language === 'en' ? 'Logout' : 'Deconectare'}</span>
                      </Logout>
                    </div>
                  </div>
                )}
              </div>

              {/* Notifications occupy the former account position. */}
              <NotificationBell />

              <button
                className="mobile-menu-btn mobile-only"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Deschide meniul de administrare"
              >
                <Menu size={18} />
              </button>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="admin-content">
          {renderContent()}
        </main>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div className="mobile-menu-overlay">
            <div className="mobile-menu">
              <div className="mobile-sidebar-header">
                <div className="mobile-logo-section">
                  <div className="logo-home" style={{ cursor: 'pointer' }} onClick={() => history.push('/')}>
                    <Home size={24} />
                  </div>
                  <h2 className="brand-name">Panou</h2>
                </div>
                <button
                  className="mobile-sidebar-toggle"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <X size={20} />
                </button>
              </div>

              <nav className="mobile-sidebar-nav">
                <ul className="mobile-nav-list">
                  {menuItems.map((item) => {
                    const IconComponent = item.icon;
                    return (
                      <li key={item.id} className="mobile-nav-item">
                        <button
                          className={`mobile-nav-link ${step === item.id ? 'active' : ''}`}
                          onClick={() => {
                            setStep(item.id);
                            setMobileMenuOpen(false);
                          }}
                        >
                          <IconComponent size={20} className="nav-icon" />
                          <div className="nav-content">
                            <span className="nav-label">{item.label}</span>
                            <span className="nav-description">{item.description}</span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              <div className="mobile-sidebar-footer">
                <div className="mobile-user-info">
                  <div className="user-avatar">
                    <User size={16} />
                  </div>
                  <div className="user-details">
                    <span className="user-name">{setDisplayName() || 'Utilizator Admin'}</span>
                    <span className="user-role">Administrator</span>
                  </div>
                </div>
                <Logout className="mobile-logout-btn">
                  <LogOut size={18} />
                  <span>Deconectare</span>
                </Logout>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
